import type { Rank } from "@rivaled/shared";
import { RANK_ORDER } from "@rivaled/shared";

import { cn } from "../../lib/utils";
import { Input } from "../ui";
import { RankIcon, rankColors, rankVarAlpha } from "../ui/RankIcon";
import type { QueueSettingsForm } from "./useQueueSettingsForm";

type Props = {
  form: QueueSettingsForm;
};

const thresholdRanks = RANK_ORDER.filter((r) => r !== "prime" && r !== "champion") as Exclude<
  Rank,
  "prime" | "champion"
>[];

export function RanksSection({ form }: Props) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-surface-500">
        Set the minimum ELO required for each rank tier. Players are assigned the highest rank whose
        threshold they meet. Leave blank for system defaults.
      </p>

      {/* Rank thresholds */}
      <div className="space-y-2">
        {thresholdRanks.map((tier) => {
          const colors = rankColors[tier];
          const isUnranked = tier === "unranked";

          return (
            <div
              key={tier}
              className={cn(
                "flex items-center gap-4 rounded-lg px-4 py-3 border transition-colors duration-200",
                "bg-surface-800/40 border-surface-700/30",
                !isUnranked && "hover:border-surface-600/50",
              )}
              style={
                !isUnranked
                  ? {
                      borderColor: form.rankThresholds[tier] ? rankVarAlpha(tier, 30) : undefined,
                    }
                  : undefined
              }
            >
              <div className="shrink-0">
                <RankIcon rank={tier} size="sm" showLevels={false} />
              </div>
              <span
                className={cn(
                  "text-sm font-display font-bold uppercase tracking-wider w-24 shrink-0",
                  colors.text,
                )}
              >
                {colors.name}
              </span>
              <div className="flex-1 max-w-48">
                {isUnranked ? (
                  <span className="text-xs text-surface-500 italic">Base rank (no threshold)</span>
                ) : (
                  <Input
                    type="number"
                    value={form.rankThresholds[tier] ?? ""}
                    onChange={(e) =>
                      form.setRankThresholds((prev) => ({ ...prev, [tier]: e.target.value }))
                    }
                    placeholder="Default"
                    suffix="ELO"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Prime & Champion — position-based */}
      <div className="border-t border-separator pt-5 space-y-3">
        <div>
          <p className="text-sm font-bold text-white">Position-Based Ranks</p>
          <p className="text-xs text-surface-500 mt-0.5">
            Prime and Champion are awarded to the top N players by rating, overriding
            threshold-based ranks.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Prime */}
          <div
            className="rounded-lg px-4 py-4 border transition-colors duration-200"
            style={{
              backgroundColor: rankVarAlpha("prime", 5),
              borderColor: rankVarAlpha("prime", 20),
            }}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <RankIcon rank="prime" size="sm" showLevels={false} />
              <span className="text-sm font-display font-bold uppercase tracking-wider text-rank-prime">
                Prime
              </span>
            </div>
            <Input
              label="Top N Players"
              type="number"
              value={form.primeTopN}
              onChange={(e) => form.setPrimeTopN(e.target.value)}
              placeholder="Default"
              min={1}
            />
          </div>

          {/* Champion */}
          <div
            className="rounded-lg px-4 py-4 border transition-colors duration-200"
            style={{
              backgroundColor: rankVarAlpha("champion", 5),
              borderColor: rankVarAlpha("champion", 20),
            }}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <RankIcon rank="champion" size="sm" showLevels={false} />
              <span className="text-sm font-display font-bold uppercase tracking-wider text-rank-champion">
                Champion
              </span>
            </div>
            <Input
              label="Top N Players"
              type="number"
              value={form.championTopN}
              onChange={(e) => form.setChampionTopN(e.target.value)}
              placeholder="Default"
              min={1}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
